import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import moment from "moment";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import { loadStripe } from "@stripe/stripe-js";
// @ts-ignore
import html2pdf from "html2pdf.js";
import { shouldUseNewPdfFormat } from "utils/shouldUseNewPdfFormat";
import { formatAmount } from "utils/formatAmount";
import RentReceipt from "./RentReciept";
import ClientGlobalInvoice from "./invoice/ClientGlobalInvoice";
import logo from "./Ch/newlogoeuropea.png";
import { base_url } from "../../constant/constant";

import calendarIcon from "images/pay/calendar.svg";
import bedIcon from "images/card/bed-icon.svg";
import bathIcon from "images/card/bath-icon.svg";
import sleepsIcon from "images/card/sleeps-icon.svg";
import pinIconGold from "images/pay/pin-icon.svg";
import arrowRightIcon from "images/footer/arrow-right.svg";
import parisIcon from "images/header/paris-icon.svg";
import brusselsIcon from "images/header/brussels-icon.svg";
import chevronDownIcon from "images/pay/chevron-down.svg";
import planeIcon from "images/pay/plane-icon.svg";
import ContactFormModal from "shared/Footer/ContactForm";
import useWindowSize from "hooks/useWindowResize";
import { useLocale } from "i18n/locale";
import { parseDestinationGuide } from "./destinationGuide";
import LanguageSwitcher from "components/Header/LanguageSwitcher";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export interface MyBookingsProps {
  className?: string;
  property_id?: {};
}

interface Property {
  title: string;
  licence: String;
}
interface BookingData {
  property_id: Property | null;
  guest_name: string;
  company_invoice: string;
  number_of_person: string;
  rent: string;
  guest_phone: string;
  status: string;
  comment: string;
  channel_name: string;
  city_tax: any;
  city_tax_status: any;
  cleaning_fees: any;
  cleaning_fees_status: any;
  service_fees: any;
  service_fees_status: any;
  utilities_fees: any;
  extra_fees: any;
  extra_fees_status: any;
  rent_status: any;
  raw_rent: any;
  vat_on_rent: any;
  vat_status: any;
  gross_amount: any;
  start_date: any;
  end_date: any;
  images: any;
}

interface PropertyDetails {
  _id: any;
  title: string;
  reference_no: string;
  address: string;
  check_in_time: any;
  check_out_time: any;
  bedrooms: any;
  bathrooms: any;
  adults: any;
  total_size: any;
  image: any;
  description: {
    en: any;
    fr: any;
  };
}

/** One city's concierge guide, as written in the DMS destination editor. */
interface DestinationRecord {
  content?: string;
  content_fr?: string;
  city?: string;
}

interface AddBookingCommentResponse {
  title: string;
  error: boolean;
  message?: string;
}

const MyBookings: FC<MyBookingsProps> = ({ className = "" }) => {
  // This page has no responsive layout by default (fixed 282px side padding,
  // fixed column widths, non-wrapping flex rows). Since the whole page is
  // inline-styled, we derive breakpoints from window width instead of CSS media
  // queries and switch the problem styles below.
  const { t, locale } = useLocale();
  const { width: winWidth } = useWindowSize();
  const isMobile = winWidth < 768;
  // The Booking Details / Cost Summary columns are 408px each; side-by-side they
  // need ~820px of content and overflow on tablets, so we stack them below 1024.
  const isNarrow = winWidth < 1024;
  // On small phones the check-in / check-out dates wrap ("14 Aug," / "2026")
  // because each half of the box runs out of room; tighten its padding/gap here.
  const isSmallPhone = winWidth < 480;
  const sidePad =
    winWidth >= 1280 ? 282 : winWidth >= 1024 ? 120 : winWidth >= 768 ? 60 : winWidth >= 480 ? 24 : 16;

  const [selectedId, setSelectedId] = useState<string | "">();
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [companyInfo, setCompanyInfo] = useState<any>(null);
  const [images, setImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState("");
  const [comment, setComment] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [propertId, setPropertyId] = useState<PropertyDetails>();
  const contentRef = useRef<HTMLDivElement>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [destination, setDestination] = useState<DestinationRecord | null>(null);
  const guideContentRef = useRef<HTMLDivElement>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    isChecked: false,
  });

  const getBookingDetails = async () => {
    try {
      const url = new URL(window.location.href);
      const id = url.searchParams.get("id");
      const response = await axios.get(
        `${base_url}/booking/booking-detatils?id=${id}`
      );
      const bookData = response.data.data;
      setBookingData(bookData);
      setImages(bookData.images);
      const companyCity =
        bookData?.property_id?.location || bookData?.property_id?.city;
      if (companyCity) {
        try {
          const companyRes = await axios.get(
            `${base_url}/company/get_company?city=${encodeURIComponent(
              companyCity
            )}`
          );
          setCompanyInfo(companyRes?.data?.data?.[0] || null);
        } catch (companyErr) {
          console.error("Error fetching company info:", companyErr);
        }
      }
      setComment(bookData?.comment);
      setPropertyId(bookData.property_id);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching booking details:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    const id = url.searchParams.get("id");
    if (id) {
      getBookingDetails();
    } else {
      // Temporary: fetch a random property for design preview
      axios
        .get(`${base_url}/property/website_property_listing?page=1&limit=1`)
        .then((response) => {
          const prop = response.data?.data?.[0];
          if (prop) {
            setBookingData({
              property_id: { title: prop.title || "Europea Residence", licence: "" },
              guest_name: "John Doe",
              company_invoice: "EUR-2026-001",
              number_of_person: "4",
              rent: "675.00",
              guest_phone: "+32 000 000 000",
              status: "not paid",
              comment: "",
              channel_name: "Europea",
              city_tax: "25.00",
              city_tax_status: "not paid",
              cleaning_fees: "50.00",
              cleaning_fees_status: "not paid",
              service_fees: "0",
              service_fees_status: "not paid",
              utilities_fees: "0",
              extra_fees: "0",
              extra_fees_status: "not paid",
              rent_status: "not paid",
              raw_rent: "675.00",
              vat_on_rent: "0",
              vat_status: "not paid",
              gross_amount: "750.00",
              start_date: moment().add(7, "days").toISOString(),
              end_date: moment().add(14, "days").toISOString(),
              images: [prop.image],
            } as any);
            setImages([prop.image]);
            setPropertyId({
              _id: prop._id || "preview",
              title: prop.title || "Europea Residence",
              reference_no: "",
              address: prop.address || "Lisbon, Portugal",
              check_in_time: "15:00",
              check_out_time: "11:00",
              bedrooms: prop.bedrooms || "2",
              bathrooms: prop.bathrooms || "1",
              adults: "4",
              total_size: "85",
              image: prop.image || "",
              description: { en: "", fr: "" },
            });
          }
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, []);

  // ----- PDF generation -----
  const generatePDF = async () => {
    if (!contentRef.current) return;

    const title = `${moment(invoiceReservationData?.end_date).utc().format("DDMMYY")} - ${invoiceReservationData?.company_invoice || ''} - ${propertId?.title || ''} - Reservation Invoice`;

    const options = {
      margin: 10,
      filename: title,
      image: { type: "jpeg", quality: 1.0 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    if (useNewInvoice) {
      const worker: any = await html2pdf()
        .set(options)
        .from(contentRef.current)
        .toPdf()
        .get("pdf");

      const pageCount = worker.internal.getNumberOfPages();
      const pageHeight = worker.internal.pageSize.getHeight();
      const pageWidth = worker.internal.pageSize.getWidth();
      const footerText =
        "EUROPEA Residences SA, 15 Monts des arts, 1000 Bruxelles BE0643510470";
      for (let i = 1; i <= pageCount; i++) {
        worker.setPage(i);
        worker.setFontSize(9);
        worker.setTextColor(150);
        const textWidth =
          (worker.getStringUnitWidth(footerText) *
            worker.internal.getFontSize()) /
          worker.internal.scaleFactor;
        const x = (pageWidth - textWidth) / 2;
        worker.text(footerText, x, pageHeight - 9);
      }

      worker.save(title);
      return;
    }

    await html2pdf(contentRef.current, options);
  };

  // The banner greets the guest by first name. `guest_name` holds whatever the
  // channel sent — sometimes a full name, sometimes one word, sometimes blank —
  // so take the leading word and fall back to the generic welcome when there is
  // nothing usable rather than greeting "Welcome, ".
  const guestFirstName = (bookingData?.guest_name || "").trim().split(/\s+/)[0] || "";

  // ----- Concierge guide -----
  // The property's city decides which guide the guest sees: a Paris booking
  // gets the Paris guide, a Brussels booking the Brussels one. A property with
  // no city on it falls back to Paris, as the previous site did — the wrong
  // city's guide is a smaller failure than no guide at all. Undefined until the
  // booking has loaded, so the lookup runs once, with the real city.
  const guideCity: string | undefined = bookingData
    ? (bookingData.property_id as any)?.location ||
      (bookingData.property_id as any)?.city ||
      "Paris"
    : undefined;

  useEffect(() => {
    if (!guideCity) return;
    let cancelled = false;
    axios
      .get(
        `${base_url}/propertyDestination/list_destination?city=${encodeURIComponent(
          guideCity
        )}`
      )
      .then((response) => {
        if (cancelled) return;
        setDestination(response.data?.data?.[0] || null);
      })
      .catch((err) => {
        // The guide is supplementary — a booking page still has to render its
        // dates and costs if the destination lookup fails.
        console.error("Error fetching destination guide:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [guideCity]);

  // Both languages arrive in the same record, so switching language re-reads
  // what is already in memory instead of refetching. A city whose French copy
  // has not been written yet falls back to English rather than going blank.
  const guide = useMemo(() => {
    if (!destination) return null;
    const html =
      locale === "fr"
        ? destination.content_fr || destination.content
        : destination.content;
    return parseDestinationGuide(html);
  }, [destination, locale]);

  const guideCityLabel = useMemo(() => {
    const city = (guideCity || "").trim().toLowerCase();
    if (city === "paris") return t("common.paris");
    if (city === "brussels" || city === "bruxelles") return t("common.brussels");
    return guideCity || "";
  }, [guideCity, t]);

  const guideIcon = /brux|bruss/i.test(guideCity || "") ? brusselsIcon : parisIcon;

  // ----- Stripe payment -----
  const stripePublicKey = "pk_live_51QNtMrDypEZS83khOQXPeoNLaZXE955nCTrSpYC6xeqnOJH3bNV1WCiNaoGCC0P7xMMnaFrYmpBTZQ7azL4CF5mH00YTg4tPTA";

    // const stripePublicKey = "pk_test_51QRSaBDgf9hHTePcbPg5OZ0Updg5nJbJ5e9tdtj9vphrVeCxXQ8bVmT9nM0Y74XuMTlqPWVy3to8TnNVALZdG3N700QdyeERC0"; //developer
  
    const handlePayment = async (amount: number): Promise<void> => {
    try {
      const payAmount = Number(bookingData?.rent);
      if (payAmount <= 0) {
        setSnackbarSeverity("error");
        setSnackbarMessage("Amount should be greater than zero");
        return setSnackbarOpen(true);
      }
      const url = new URL(window.location.href);
      const id = url.searchParams.get("id");
      if (id) {
        setIsProcessing(true);
        const stripe = await loadStripe(stripePublicKey);
        if (!stripe) {
          throw new Error("Stripe failed to load.");
        }

        const { data } = await axios.post(
          `${base_url}/stripe/create-checkout-session`,
          {
            amount: payAmount,
            currency: "eur",
            bookingId: id,
          }
        );

        if (!data.sessionId) {
          throw new Error("Failed to create a checkout session.");
        }

        const result = await stripe.redirectToCheckout({
          sessionId: data.sessionId,
        });

        if (result.error) {
          throw new Error(
            `Stripe Checkout redirection failed: ${result.error.message}`
          );
        }
      } else {
        setSnackbarSeverity("error");
        setSnackbarMessage("Error: Booking id is missing");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setMessage(`Error: ${(error as Error).message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // ----- Comment submission -----
  const handleSubmit = async (): Promise<AddBookingCommentResponse> => {
    try {
      const url = new URL(window.location.href);
      const id = url.searchParams.get("id");
      if (!id || !comment) {
        setSnackbarSeverity("error");
        setSnackbarMessage("Booking ID or comment is missing.");
        setSnackbarOpen(true);
        return { title: "Booking ID or comment is missing.", error: true };
      }

      const data = { comment, id };
      const response = await axios.post<AddBookingCommentResponse>(
        `${base_url}/booking/add_booking_comment`,
        data
      );

      if (response?.data.error) {
        setSnackbarSeverity("error");
        setSnackbarMessage(response?.data.title || "An error occurred.");
      } else {
        setSnackbarSeverity("success");
        setSnackbarMessage(
          response?.data.title || "Comment added successfully."
        );
      }

      setSnackbarOpen(true);
      return response.data;
    } catch (error: any) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Something went wrong");
      setSnackbarOpen(true);

      if (error.response) {
        return error.response.data;
      }

      throw new Error("Something went wrong");
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // ----- Reservation invoice wiring -----
  const toInvoiceNumber = (val: any): number => {
    const n = parseFloat(String(val).replace(",", "."));
    return isNaN(n) ? 0 : n;
  };

  const anyBooking = bookingData as any;
  const invoiceReservationData = bookingData
    ? {
        ...anyBooking,
        rent: (
          toInvoiceNumber(anyBooking?.rent) -
          toInvoiceNumber(anyBooking?.vat_on_rent)
        ).toFixed(2),
        property_details: anyBooking?.property_id,
      }
    : null;

  const invoiceEnd = anyBooking?.end_date ? moment(anyBooking.end_date) : null;
  const useNewInvoice = !!invoiceEnd
    ? shouldUseNewPdfFormat(
        invoiceEnd.month() + 1,
        invoiceEnd.year(),
      )
    : false;

  // ----- Render -----

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fff",
          fontFamily: "Poppins, sans-serif",
          fontSize: 18,
          color: "#01152E",
        }}
      >
        {t("common.loading")}
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      <Helmet>
        <title>My Booking</title>
      </Helmet>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Logo header — this page opts out of the site header (see
          HIDDEN_LAYOUT_ROUTES in routers/AppLayout), so the language switcher
          is placed here too: without it a guest sent straight to their booking
          link has no way to change language. */}
      <div
        style={{
          padding: `22px ${sidePad}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <a href="/">
          <img
            src="/logo/europea-residences-group.svg"
            alt="Europea"
            style={{ width: 129, height: 32 }}
          />
        </a>
        <LanguageSwitcher />
      </div>

      {/* Content area */}
      <div style={{ padding: `0 ${sidePad}px` }}>
        {/* Teal box (no icon) */}
        <div
          style={{
            backgroundColor: "#01152E",
            padding: isMobile ? 24 : 40,
            borderRadius: 0,
          }}
        >
          <p
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "16px",
              color: "#B88333",
              textTransform: "uppercase",
              letterSpacing: "1.4px",
              margin: "0 0 4px 0",
            }}
          >
            {t("myBookings.stayConfirmed")}
          </p>
          <p
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              fontSize: 24,
              lineHeight: "28px",
              color: "#fff",
              margin: "0 0 4px 0",
            }}
          >
            {guestFirstName
              ? t("myBookings.welcomeNamed", { name: guestFirstName })
              : t("myBookings.welcome")}
          </p>
          <p
            style={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 400,
              fontSize: 14,
              lineHeight: "16px",
              color: "#D6D6D6",
              margin: 0,
            }}
          >
            {t("myBookings.subheading")}
          </p>
        </div>

        {/* Property row */}
        <div style={{ display: "flex", flexDirection: isNarrow ? "column" : "row", gap: isNarrow ? 20 : 30, marginTop: 30 }}>
          {/* Property image */}
          <img
            src={images?.[0]}
            alt={propertId?.title}
            style={{
              width: isNarrow ? "100%" : 300,
              height: 200,
              borderRadius: 10,
              objectFit: "cover",
              flexShrink: 0,
            }}
          />

          {/* Property details */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
            <h2
              style={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
                fontSize: 24,
                lineHeight: "28px",
                letterSpacing: "0.48px",
                color: "#01152E",
                margin: 0,
              }}
            >
              {propertId?.title}
            </h2>

            {/* Location */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 12,
              }}
            >
              <img src={pinIconGold} alt="" style={{ width: 9, height: 11 }} />
              <span
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 400,
                  fontSize: 14,
                  lineHeight: "24px",
                  color: "#445162",
                }}
              >
                {propertId?.address}
              </span>
            </div>

            {/* Amenities row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                rowGap: 6,
                gap: 12,
                fontFamily: "Poppins, sans-serif",
                fontWeight: 400,
                fontSize: 14,
                lineHeight: "24px",
                color: "#445162",
                marginBottom: 24,
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <img src={bedIcon} alt="" style={{ width: 19, height: 14 }} />
                <span>
                  {propertId?.bedrooms ?? 1} {t("payDone.bedrooms")}
                </span>
              </div>
              <span style={{ color: "#D6D6D6" }}>|</span>
              <div
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <img
                  src={bathIcon}
                  alt=""
                  style={{ width: 14, height: 14 }}
                />
                <span>
                  {propertId?.bathrooms ?? 1} {t("payDone.bathrooms")}
                </span>
              </div>
              <span style={{ color: "#D6D6D6" }}>|</span>
              <div
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <img
                  src={sleepsIcon}
                  alt=""
                  style={{ width: 18, height: 14 }}
                />
                <span>{t("payDone.sleeps")} {bookingData?.number_of_person}</span>
              </div>
            </div>

            {/* Check-in / Check-out box */}
            <div
              style={{
                border: "1px solid #D6D6D6",
                borderRadius: 8,
                display: "flex",
                height: "auto",
                minHeight: 72,
              }}
            >
              {/* Check-in section */}
              <div style={{ flex: 1, minWidth: 0, padding: isSmallPhone ? "10px 12px" : "14px 20px", display: "flex", alignItems: "center", gap: isSmallPhone ? 10 : 16 }}>
                <img
                  src={calendarIcon}
                  alt=""
                  style={{ width: 26, height: 27, flexShrink: 0 }}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  <span
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: "24px",
                      color: "#445162",
                      textTransform: "uppercase",
                      letterSpacing: "1.2px",
                    }}
                  >
                    {t("myBookings.checkIn")}
                  </span>
                  <p
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 600,
                      fontSize: 16,
                      lineHeight: "24px",
                      color: "#01152E",
                      margin: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {moment(bookingData?.start_date).format("DD MMM, YYYY")}
                  </p>
                  {propertId?.check_in_time && (
                    <span
                      style={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 400,
                        fontSize: 12,
                        lineHeight: "18px",
                        color: "#445162",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("myBookings.from")} {moment(propertId.check_in_time, "HH:mm").format("h:mm A")}
                    </span>
                  )}
                </div>
              </div>

              {/* Vertical divider */}
              <div style={{ width: 1, backgroundColor: "#D6D6D6" }} />

              {/* Check-out section */}
              <div style={{ flex: 1, minWidth: 0, padding: isSmallPhone ? "10px 12px" : "14px 20px", display: "flex", alignItems: "center", gap: isSmallPhone ? 10 : 16 }}>
                <img
                  src={calendarIcon}
                  alt=""
                  style={{ width: 26, height: 27, flexShrink: 0 }}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  <span
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: "24px",
                      color: "#445162",
                      textTransform: "uppercase",
                      letterSpacing: "1.2px",
                    }}
                  >
                    {t("myBookings.checkOut")}
                  </span>
                  <p
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 600,
                      fontSize: 16,
                      lineHeight: "24px",
                      color: "#01152E",
                      margin: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {moment(bookingData?.end_date).format("DD MMM, YYYY")}
                  </p>
                  {propertId?.check_out_time && (
                    <span
                      style={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 400,
                        fontSize: 12,
                        lineHeight: "18px",
                        color: "#445162",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("myBookings.by")} {moment(propertId.check_out_time, "HH:mm").format("h:mm A")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Full-width separator above table */}
        <div style={{ height: 1, backgroundColor: "#01152E", marginTop: 30 }} />

        {/* Two-column table: Booking Details + Cost Summary. No horizontal gap in
            row mode so the dark header underlines run all the way to the center
            divider (connected frame). The grey row-separator lines and the text
            live inside an inner block that is padded on its divider-facing side,
            so they stop short of the divider — matching the live design. */}
        <div style={{ display: "flex", flexDirection: isNarrow ? "column" : "row", gap: isNarrow ? 24 : 0 }}>
          {/* Left column: Booking Details */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3
              style={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
                fontSize: 18,
                lineHeight: "24px",
                letterSpacing: "0.36px",
                color: "#01152E",
                margin: 0,
                padding: "20px 0",
              }}
            >
              {t("myBookings.bookingDetails")}
            </h3>
            <div style={{ height: 1, backgroundColor: "#01152E" }} />

            <div style={{ width: "100%", paddingRight: isNarrow ? 0 : 40 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "20px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#01152E",
                  }}
                >
                  {t("myBookings.clientName")}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#445162",
                  }}
                >
                  {bookingData?.guest_name}
                </span>
              </div>

              <div style={{ height: 1, backgroundColor: "#D6D6D6" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "20px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#01152E",
                  }}
                >
                  {t("myBookings.reservationNumber")}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#445162",
                  }}
                >
                  {bookingData?.company_invoice}
                </span>
              </div>

              <div style={{ height: 1, backgroundColor: "#D6D6D6" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "20px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#01152E",
                  }}
                >
                  {t("common.guests")}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#445162",
                  }}
                >
                  {bookingData?.number_of_person}
                </span>
              </div>

              <div style={{ height: 1, backgroundColor: "#D6D6D6" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "20px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#01152E",
                  }}
                >
                  {t("myBookings.bookingChannel")}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#445162",
                  }}
                >
                  {bookingData?.channel_name}
                </span>
              </div>
              <div style={{ height: 1, backgroundColor: "#D6D6D6" }} />
            </div>
          </div>

          {/* Vertical divider */}
          <div style={{ width: 1, backgroundColor: "#01152E", alignSelf: "stretch", display: isNarrow ? "none" : "block" }} />

          {/* Right column: Cost Summary */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3
              style={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
                fontSize: 18,
                lineHeight: "24px",
                letterSpacing: "0.36px",
                color: "#01152E",
                margin: 0,
                padding: isNarrow ? "20px 0" : "20px 0 20px 40px",
              }}
            >
              {t("myBookings.costSummary")}
            </h3>
            <div style={{ height: 1, backgroundColor: "#01152E" }} />

            <div style={{ width: "100%", paddingLeft: isNarrow ? 0 : 40 }}>
              {/* Rent */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "20px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#01152E",
                    flex: 1,
                  }}
                >
                  {t("myBookings.rent")}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#445162",
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  &euro; {formatAmount(Number(bookingData?.raw_rent || 0), locale)}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 10,
                    lineHeight: "14px",
                    color: bookingData?.rent_status === "paid" ? "#3DBB3F" : "#EB6B63",
                    backgroundColor: bookingData?.rent_status === "paid" ? "rgba(61,187,63,0.16)" : "rgba(235,107,99,0.16)",
                    borderRadius: 10,
                    padding: "4px 8px",
                    whiteSpace: "nowrap" as const,
                  }}
                >
                  {bookingData?.rent_status === "paid" ? t("myBookings.paid") : t("myBookings.notPaid")}
                </span>
              </div>

              <div style={{ height: 1, backgroundColor: "#D6D6D6" }} />
              {/* Service fees */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "20px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#01152E",
                    flex: 1,
                  }}
                >
                  {t("myBookings.serviceFees")}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#445162",
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  &euro; {formatAmount(Number(bookingData?.cleaning_fees || 0), locale)}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 10,
                    lineHeight: "14px",
                    color: bookingData?.cleaning_fees_status === "paid" ? "#3DBB3F" : "#EB6B63",
                    backgroundColor: bookingData?.cleaning_fees_status === "paid" ? "rgba(61,187,63,0.16)" : "rgba(235,107,99,0.16)",
                    borderRadius: 10,
                    padding: "4px 8px",
                    whiteSpace: "nowrap" as const,
                  }}
                >
                  {bookingData?.cleaning_fees_status === "paid" ? t("myBookings.paid") : t("myBookings.notPaid")}
                </span>
              </div>

              <div style={{ height: 1, backgroundColor: "#D6D6D6" }} />
              {/* Cleaning fees */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "20px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#01152E",
                    flex: 1,
                  }}
                >
                 {t("myBookings.utilities")}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#445162",
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  &euro; {formatAmount(Number(bookingData?.utilities_fees || 0), locale)}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 10,
                    lineHeight: "14px",
                    color: bookingData?.service_fees_status === "paid" ? "#3DBB3F" : "#EB6B63",
                    backgroundColor: bookingData?.service_fees_status === "paid" ? "rgba(61,187,63,0.16)" : "rgba(235,107,99,0.16)",
                    borderRadius: 10,
                    padding: "4px 8px",
                    whiteSpace: "nowrap" as const,
                  }}
                >
                  {bookingData?.service_fees_status === "paid" ? t("myBookings.paid") : t("myBookings.notPaid")}
                </span>
              </div>

              <div style={{ height: 1, backgroundColor: "#D6D6D6" }} />
              {/* Extra fees */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "20px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#01152E",
                    flex: 1,
                  }}
                >
                  {t("myBookings.extraFees")}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#445162",
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  &euro; {formatAmount(Number(bookingData?.extra_fees || 0), locale)}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 10,
                    lineHeight: "14px",
                    color: bookingData?.extra_fees_status === "paid" ? "#3DBB3F" : "#EB6B63",
                    backgroundColor: bookingData?.extra_fees_status === "paid" ? "rgba(61,187,63,0.16)" : "rgba(235,107,99,0.16)",
                    borderRadius: 10,
                    padding: "4px 8px",
                    whiteSpace: "nowrap" as const,
                  }}
                >
                  {bookingData?.extra_fees_status === "paid" ? t("myBookings.paid") : t("myBookings.notPaid")}
                </span>
              </div>


              <div style={{ height: 1, backgroundColor: "#D6D6D6" }} />
              {/* City Tax */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "20px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#01152E",
                    flex: 1,
                  }}
                >
                  {t("myBookings.cityTax")}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#445162",
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  &euro; {formatAmount(Number(bookingData?.city_tax || 0), locale)}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 10,
                    lineHeight: "14px",
                    color: bookingData?.city_tax_status === "paid" ? "#3DBB3F" : "#EB6B63",
                    backgroundColor: bookingData?.city_tax_status === "paid" ? "rgba(61,187,63,0.16)" : "rgba(235,107,99,0.16)",
                    borderRadius: 10,
                    padding: "4px 8px",
                    whiteSpace: "nowrap" as const,
                  }}
                >
                  {bookingData?.city_tax_status === "paid" ? t("myBookings.paid") : t("myBookings.notPaid")}
                </span>
              </div>

              <div style={{ height: 1, backgroundColor: "#D6D6D6" }} />
              {/* VAT — value from vat_on_rent, status follows the rent (matches
                  the legacy site). */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "20px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#01152E",
                    flex: 1,
                  }}
                >
                  {t("myBookings.vat")}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#445162",
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  &euro; {formatAmount(Number(bookingData?.vat_on_rent || 0), locale)}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 10,
                    lineHeight: "14px",
                    color: bookingData?.rent_status === "paid" ? "#3DBB3F" : "#EB6B63",
                    backgroundColor: bookingData?.rent_status === "paid" ? "rgba(61,187,63,0.16)" : "rgba(235,107,99,0.16)",
                    borderRadius: 10,
                    padding: "4px 8px",
                    whiteSpace: "nowrap" as const,
                  }}
                >
                  {bookingData?.rent_status === "paid" ? t("myBookings.paid") : t("myBookings.notPaid")}
                </span>
              </div>

              <div style={{ height: 1, backgroundColor: "#D6D6D6" }} />
              {/* Total */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "20px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    fontSize: 18,
                    lineHeight: "24px",
                    color: "#01152E",
                  }}
                >
                  {t("payDone.total")}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    fontSize: 18,
                    lineHeight: "24px",
                    color: "#01152E",
                  }}
                >
                  &euro; {formatAmount(
                    Number(bookingData?.gross_amount || 0) +
                    Number(bookingData?.city_tax || 0),
                    locale
                  )}
                </span>
              </div>
              <div style={{ height: 1, backgroundColor: "#D6D6D6" }} />
            </div>
          </div>
        </div>

        {/* Full-width separator below table */}
        <div style={{ height: 1, backgroundColor: "#01152E" }} />

        {/* Arrival details */}
        <p
          style={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 600,
            fontSize: 16,
            lineHeight: "24px",
            color: "#01152E",
            margin: "30px 0 16px 0",
          }}
        >
          {t("myBookings.arrivalDetailsPrompt")}
        </p>
        <div
          style={{
            border: "1px solid #D6D6D6",
            borderRadius: 8,
            height: isNarrow ? "auto" : 90,
            display: "flex",
            flexDirection: isNarrow ? "column" : "row",
            alignItems: isNarrow ? "stretch" : "center",
            gap: isNarrow ? 12 : 0,
            padding: isNarrow ? 16 : "0 20px",
          }}
        >
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onFocus={(e) => { e.target.style.outline = "none"; }}
            placeholder={t("myBookings.arrivalDetailsPlaceholder")}
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 400,
              fontSize: 14,
              lineHeight: "20px",
              color: "#01152E",
              border: "none",
              outline: "0",
              boxShadow: "none",
              resize: "none",
              flex: 1,
              width: "100%",
              height: isNarrow ? 64 : "100%",
              paddingTop: isNarrow ? 4 : 20,
              backgroundColor: "transparent",
              WebkitAppearance: "none",
            }}
          />
          <button
            onClick={handleSubmit}
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "24px",
              color: "#01152E",
              backgroundColor: "transparent",
              border: "1px solid #01152E",
              borderRadius: 22,
              padding: "8px 24px",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {t("myBookings.submitDetails")}
          </button>
        </div>

        {/* Separator */}
        <div style={{ height: 1, backgroundColor: "#01152E", marginTop: 30 }} />

        {/* Disclaimer + Pay Now */}
        <div
          style={{
            display: "flex",
            flexDirection: isNarrow ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isNarrow ? "flex-start" : "center",
            gap: isNarrow ? 16 : 0,
            marginTop: 24,
            paddingBottom: 40,
          }}
        >
          <p
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 400,
              fontSize: 14,
              lineHeight: "18px",
              color: "#445162",
              margin: 0,
              maxWidth: 500,
            }}
          >
            {t("myBookings.commissionDisclaimer")}
          </p>
          {/* When every part of the booking is paid, offer the invoice
              download instead of the payment CTA (matches the legacy site). */}
          {bookingData?.status === "paid" &&
          bookingData?.rent_status === "paid" &&
          bookingData?.extra_fees_status === "paid" &&
          bookingData?.city_tax_status === "paid" &&
          bookingData?.cleaning_fees_status === "paid" &&
          bookingData?.service_fees_status === "paid" ? (
            <button
              onClick={generatePDF}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                width: 185,
                height: 44,
                backgroundColor: "#01152E",
                borderRadius: 22,
                fontFamily: "Poppins, sans-serif",
                fontSize: 14,
                fontWeight: 500,
                lineHeight: "24px",
                color: "#fff",
                border: "none",
                cursor: "pointer",
              }}
            >
              {t("myBookings.downloadInvoice")}
            </button>
          ) : (
            <button
              onClick={() => handlePayment(Number(bookingData?.rent))}
              disabled={isProcessing}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                width: 165,
                height: 44,
                backgroundColor: "#01152E",
                borderRadius: 22,
                fontFamily: "Poppins, sans-serif",
                fontSize: 14,
                fontWeight: 500,
                lineHeight: "24px",
                color: "#fff",
                border: "none",
                cursor: isProcessing ? "not-allowed" : "pointer",
                opacity: isProcessing ? 0.7 : 1,
              }}
            >
              {isProcessing ? t("myBookings.processing") : t("myBookings.payNow")}
              {!isProcessing && (
                <img
                  src={arrowRightIcon}
                  alt=""
                  style={{ width: 13, height: 12 }}
                />
              )}
            </button>
          )}
        </div>

        {/* Concierge Guide — content comes from the DMS destination editor */}
        {guide && guide.cards.length > 0 && (
        <div
          style={{
            backgroundColor: "#F5EEE4",
            borderRadius: 12,
            padding: isMobile ? "28px 20px" : "50px 40px",
          }}
        >
        {/*
          The card bodies are admin-authored HTML, so their formatting is styled
          by rule here rather than by props on each element.
        */}
        <style>{`
          .europea-guide-body {
            font-family: Roboto, sans-serif;
            font-weight: 400;
            font-size: 16px;
            line-height: 24px;
            color: #445162;
          }
          .europea-guide-body h1,
          .europea-guide-body h2,
          .europea-guide-body h3,
          .europea-guide-body h4,
          .europea-guide-body h5,
          .europea-guide-body h6 {
            font-family: Poppins, sans-serif;
            font-weight: 600;
            font-size: 18px;
            line-height: 24px;
            letter-spacing: 0.36px;
            color: #01152E;
            margin: 30px 0 12px 0;
          }
          .europea-guide-body p { margin: 0 0 16px 0; }
          /*
            Tailwind's preflight resets ul/ol to list-style:none, so the markers
            have to be asked for again by name — without this the guide's
            bullet lists render as a run of unindented paragraphs.
          */
          .europea-guide-body ul,
          .europea-guide-body ol {
            margin: 0 0 16px 0;
            /*
              Indents the list under its heading, as the editor shows it. In em
              so the step holds at any font size, and so a nested list steps in
              again by the same amount.
            */
            padding-left: 2.4em;
            list-style-position: outside;
          }
          .europea-guide-body ul { list-style-type: disc; }
          .europea-guide-body ol { list-style-type: decimal; }
          .europea-guide-body ul ul { list-style-type: circle; }
          .europea-guide-body li {
            display: list-item;
            margin: 0 0 8px 0;
          }
          .europea-guide-body li::marker { color: #B88333; }
          .europea-guide-body strong,
          .europea-guide-body b { font-weight: 500; color: #01152E; }
          .europea-guide-body a { color: #B88333; text-decoration: underline; }
          .europea-guide-body img { max-width: 100%; height: auto; border-radius: 8px; }
          .europea-guide-body blockquote {
            margin: 0 0 16px 0;
            padding-left: 16px;
            border-left: 3px solid #B88333;
          }
          .europea-guide-body > *:first-child { margin-top: 0; }
          .europea-guide-body > *:last-child { margin-bottom: 0; }
        `}</style>

        {/* Header row */}
        <div
          style={{
            display: "flex",
            flexDirection: isNarrow ? "column" : "row",
            gap: isNarrow ? 24 : 57,
            alignItems: "flex-start",
          }}
        >
          {/* Left content */}
          <div style={{ width: isNarrow ? "100%" : "auto", flex: isNarrow ? "unset" : 1, maxWidth: isNarrow ? "100%" : 561, minWidth: 0 }}>
            {/* Icon + labels row */}
            <div style={{ display: "flex", gap: 26, alignItems: "flex-start" }}>
              <img src={guideIcon} alt="" style={{ width: 51, height: 51, flexShrink: 0 }} />
              <div>
                <p
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#B88333",
                    textTransform: "uppercase",
                    letterSpacing: "3.2px",
                    margin: 0,
                  }}
                >
                  {t("myBookings.conciergeGuide")}
                </p>
                <p
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    fontSize: isMobile ? 22 : 28,
                    lineHeight: isMobile ? "32px" : "46px",
                    letterSpacing: "0.56px",
                    color: "#01152E",
                    margin: 0,
                  }}
                >
                  {t("myBookings.quickGuideTitle", { city: guideCityLabel })}
                </p>
              </div>
            </div>
            {/* Description — the guide's own opening paragraph, so it names the
                right city and speaks the reader's language. Rendered as markup
                so the admin's bold carries over; a div rather than a p because
                the editor may put more than inline content in that first block.
                Shares the card class so bold and links match the cards below. */}
            <div
              className="europea-guide-body"
              style={{ margin: "26px 0 0 0" }}
              dangerouslySetInnerHTML={{
                __html:
                  guide.description ||
                  t("myBookings.quickGuideDescription", { city: guideCityLabel }),
              }}
            />
          </div>

          {/* Read the Guide button */}
          <button
            onClick={() => setGuideOpen(!guideOpen)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              height: 44,
              padding: "10px 24px",
              backgroundColor: "transparent",
              border: "1px solid #01152E",
              borderRadius: 30,
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "24px",
              color: "#01152E",
              cursor: "pointer",
              whiteSpace: "nowrap" as const,
              flexShrink: 0,
            }}
          >
            {guideOpen ? t("myBookings.closeGuide") : t("myBookings.readGuide")}
            <img
              src={chevronDownIcon}
              alt=""
              style={{
                width: 12,
                height: 7,
                transform: guideOpen ? "rotate(180deg)" : "none",
              }}
            />
          </button>
        </div>

        {/* Guide content — slides open/close */}
        <div
          style={{
            maxHeight: guideOpen ? (guideContentRef.current?.scrollHeight || 9999) : 0,
            overflow: guideOpen ? "visible" : "hidden",
            transition: "max-height 0.5s ease-in-out",
          }}
        >
          <div ref={guideContentRef} style={{ display: "flex", flexDirection: "column", gap: 20, paddingTop: 30 }}>
            {guide.cards.map((card) => (
              <div
                key={card.num}
                style={{
                  backgroundColor: "#fff",
                  borderRadius: 12,
                  padding: isMobile ? "28px 22px" : "41px 36px 53px 36px",
                }}
              >
                {card.title && (
                  <p
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 600,
                      fontSize: 14,
                      lineHeight: "24px",
                      color: "#B88333",
                      textTransform: "uppercase",
                      letterSpacing: "2.8px",
                      margin: "0 0 30px 0",
                    }}
                  >
                    {card.num}. {card.title}
                  </p>
                )}
                <div
                  className="europea-guide-body"
                  dangerouslySetInnerHTML={{ __html: card.html }}
                />
              </div>
            ))}

            {/* Sign-off with plane */}
            {guide.footer && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  marginTop: 24,
                }}
              >
                <p
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: isMobile ? 18 : 24,
                    lineHeight: isMobile ? "28px" : "46px",
                    fontStyle: "italic",
                    color: "#01152E",
                    margin: 0,
                  }}
                >
                  {guide.footer}
                </p>
                {!isNarrow && (
                  <img
                    src={planeIcon}
                    alt=""
                    style={{
                      width: 115,
                      height: 53,
                      flexShrink: 0,
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        </div>
        )}

        {/* Questions footer */}
        <div
          style={{
            display: "flex",
            justifyContent: isMobile ? "center" : "flex-end",
            alignItems: "center",
            marginTop: 30,
            paddingBottom: 40,
          }}
        >
          <p
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 400,
              fontSize: 14,
              lineHeight: "18px",
              color: "#445162",
              margin: 0,
            }}
          >
            {t("payDone.questions")}{" "}
            <span
              onClick={() => setContactOpen(true)}
              style={{
                fontWeight: 600,
                textDecoration: "underline",
                color: "#01152E",
                cursor: "pointer",
              }}
            >
              {t("payDone.contactUs")}
            </span>
          </p>
        </div>

      </div>

      {/* Contact form modal */}
      <ContactFormModal
        isOpen={contactOpen}
        onClose={() => {
          setContactOpen(false);
          setFormValues({ name: "", email: "", phone: "", message: "", isChecked: false });
        }}
        formValues={formValues}
        setFormValues={setFormValues}
      />

      {/* Hidden invoice elements for PDF generation */}
      <div style={{ display: "none" }}>
        <RentReceipt
          generatePDF={generatePDF}
          contentRef={useNewInvoice ? undefined : contentRef}
          property_id={propertId}
          data={bookingData}
          logo={logo}
          month={7}
          year={2024}
        />
      </div>
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          width: 0,
          height: 0,
          overflow: "hidden",
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
      >
        <div style={{ position: "absolute", top: 0, left: 0, width: "820px" }}>
          {useNewInvoice && invoiceReservationData && (
            <ClientGlobalInvoice
              reservationData={invoiceReservationData}
              companyInfo={companyInfo}
              contentRef={contentRef}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MyBookings;
